"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Category } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/types";
import { createSearchTabValue } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/utils/createSearchTabValue";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import { cn } from "@/lib/utils";
import { hyphenateForTagular } from "@/utils/tagular/helpers";

type CategoryTabBarProps = {
  categories: Category[];
  location?: string;
};

export function CategoryTabBar({ categories, location }: CategoryTabBarProps) {
  const hasViewedRef = useRef(false);

  const webElement: ElementClicked["webElement"] = useMemo(
    () => ({
      elementType: ElementType.Button,
      htmlId: "products-type-buttons",
      location: "trending-products",
      position: "top",
      name: "trending-products-lob",
      text: "trending products lob",
    }),
    [],
  );

  const { elementViewed, elementClicked } = useElementEvents({ webElement });

  const clickCategoryTab = useCallback(
    (
      webElementOverrides: Partial<NonNullable<ElementClicked["webElement"]>>,
    ) => {
      elementClicked({ webElement: webElementOverrides });
    },
    [elementClicked],
  );

  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      elementViewed();
    }
  }, [inView, elementViewed]);

  return (
    <div ref={inViewRef} className="rounded-[0.625rem] bg-white">
      <TabsList className="overflow-x-auto h-auto w-auto max-w-full justify-start bg-white">
        {categories.map((category) => {
          const sanitizedValue = createSearchTabValue(
            category.categoryLabel,
            location,
          );
          const categoryLabelDashed = hyphenateForTagular(
            category.categoryLabel,
          );

          return (
            <TabsTrigger
              key={category.categoryLabel}
              value={sanitizedValue}
              onClick={() => {
                clickCategoryTab({
                  htmlId: `category-button-${categoryLabelDashed}`,
                  name: categoryLabelDashed,
                  text: category.categoryLabel,
                });
              }}
              className={cn(
                "grow-0 no-underline bg-transparent px-5 py-4",
                "text-md text-neutral-700 lg:text-base",
                "hover:text-primary",
                "data-[state=active]:bg-primary-500 data-[state=active]:font-bold",
                "first:rounded-l-[0.625rem] last:rounded-r-[0.625rem]",
              )}
            >
              {category.categoryLabel}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </div>
  );
}
